import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon, Button } from '../../../pages/DesignSystem';
import { EventSeries } from '../../../types/eventSeries.types';
import { supabaseEventService } from '../../../supabase/event.service';
import { localToUTC } from '../../../utils/timezoneUtils';
import toast from 'react-hot-toast';
import './OccurrenceEditModal.css'; // Reuse same styles

interface AddOccurrenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  series: EventSeries | null;
  onSuccess?: () => void;
}

/**
 * AddOccurrenceModal - Modal for adding an independent one-off event
 * based on a series template (same title, location, etc.)
 *
 * NOTE: This creates a STANDALONE event, NOT linked to the series.
 * The event will have its own stats and won't be affected by series modifications.
 */
const AddOccurrenceModal: React.FC<AddOccurrenceModalProps> = ({
  isOpen,
  onClose,
  series,
  onSuccess
}) => {
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with series default times
  useEffect(() => {
    if (series && isOpen) {
      // Set default start time from series
      if (series.startTime) {
        setStartTime(series.startTime);
      } else {
        setStartTime('18:00'); // Default fallback
      }

      // Set default end time from series
      if (series.endTime) {
        // endTime might be ISO string, extract HH:MM
        const endTimePart = series.endTime.includes('T')
          ? series.endTime.split('T')[1].slice(0, 5)
          : series.endTime;
        setEndTime(endTimePart);
      } else {
        setEndTime('');
      }

      // Clear date on open
      setDate('');
    }
  }, [series, isOpen]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, isSubmitting]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!series?.organizerId || !date || !startTime) {
      toast.error('Please fill in date and start time');
      return;
    }

    // Validate end time is after start time if provided
    if (endTime && endTime <= startTime) {
      toast.error('End time must be after start time');
      return;
    }

    setIsSubmitting(true);

    try {
      // 🌍 Use the series timezone for proper UTC conversion
      const seriesTimezone = series.timezone || 'Asia/Colombo';

      // Convert local time (in series timezone) to UTC ISO string
      const utcDatetimeISO = localToUTC(date, startTime, seriesTimezone);
      const newDatetime = new Date(utcDatetimeISO);

      // Create end time if provided (also convert to UTC)
      let newEndTime: string | undefined;
      if (endTime) {
        newEndTime = localToUTC(date, endTime, seriesTimezone);
      }

      // Create a STANDALONE event (no series_id) using series data as template
      await supabaseEventService.createEvent({
        organizerId: series.organizerId,
        title: series.title,
        description: series.description || undefined,
        datetime: newDatetime,
        endTime: newEndTime,
        location: series.location || undefined,
        locationName: series.locationName || undefined,
        latitude: series.latitude,
        longitude: series.longitude,
        tags: series.tags || [],
        image: series.image || undefined,
        prices: series.prices || [],
        ticketingUrl: series.ticketingUrl || undefined,
        status: series.status || 'active',
        timezone: seriesTimezone, // 🌍 Use series timezone
        // NO series_id - this is an independent event
      });

      toast.success('Independent event created!', {
        duration: 3000,
        style: {
          borderRadius: '12px',
          background: 'linear-gradient(to bottom, #E8F5E8, #F0FFF0)',
          color: '#2E7D2E',
        }
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get min date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <AnimatePresence>
      {isOpen && series && (
        <motion.div
          className="occurrence-edit-modal__overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleOverlayClick}
        >
          <motion.div
            className="occurrence-edit-modal__dialog"
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="occurrence-edit-modal__header">
              <div className="occurrence-edit-modal__header-content">
                <Icon name="CalendarPlus" size={24} className="occurrence-edit-modal__icon" />
                <div>
                  <h2 className="occurrence-edit-modal__title">Add One-Off Date</h2>
                  <p className="occurrence-edit-modal__subtitle">{series.title}</p>
                </div>
              </div>
              <button
                type="button"
                className="occurrence-edit-modal__close"
                onClick={onClose}
                disabled={isSubmitting}
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Warning message about independent event */}
            <div className="occurrence-edit-modal__warning">
              <Icon name="AlertTriangle" size={16} />
              <span>
                This will create an <strong>independent event</strong>, not linked to the series.
                It won't be affected by future series modifications.
              </span>
            </div>

            <form onSubmit={handleSubmit} className="occurrence-edit-modal__form">
              <div className="occurrence-edit-modal__field">
                <label htmlFor="add-occurrence-date" className="occurrence-edit-modal__label">
                  <Icon name="Calendar" size={16} />
                  Date
                </label>
                <input
                  id="add-occurrence-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="occurrence-edit-modal__input"
                  min={today}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="occurrence-edit-modal__time-row">
                <div className="occurrence-edit-modal__field occurrence-edit-modal__field--half">
                  <label htmlFor="add-occurrence-start-time" className="occurrence-edit-modal__label">
                    <Icon name="Clock" size={16} />
                    Start Time
                  </label>
                  <input
                    id="add-occurrence-start-time"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="occurrence-edit-modal__input"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="occurrence-edit-modal__field occurrence-edit-modal__field--half">
                  <label htmlFor="add-occurrence-end-time" className="occurrence-edit-modal__label">
                    <Icon name="Clock" size={16} />
                    End Time
                  </label>
                  <input
                    id="add-occurrence-end-time"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="occurrence-edit-modal__input"
                    disabled={isSubmitting}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <p className="occurrence-edit-modal__note">
                <Icon name="Info" size={14} />
                The event will use the same title, location, and details as the series.
              </p>

              <div className="occurrence-edit-modal__actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting || !date}
                >
                  {isSubmitting ? 'Creating...' : 'Create Event'}
                </Button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddOccurrenceModal;
